

import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import Marquee from "react-fast-marquee";
import { useIsMobile } from "@/hooks/use-mobile";

interface Testimonial {
  id: number;
  name: string;
  title: string;
  content: string;
  rating: number;
  imageUrl: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Tanner R.",
    title: "Purple Belt, Training 4x/week",
    content: "Finally, a way to actually see my game plan come together. The Game Planner helped me map out my guard pass to submission chains, and having multiple branches for A/B options feels just like a flow roll. It's made drilling with a purpose so much easier.",
    rating: 5,
    imageUrl: "/images/testimonials/tanner-r.png?v=2",
  },
  {
    id: 2,
    name: "Angela L.",
    title: "Blue Belt, Weekend Warrior",
    content: "The weekly coach insights from my training journal are weirdly accurate—and super helpful. It's like having a coach keeping tabs on my progress, even when I'm training solo or can't make it to class.",
    rating: 5,
    imageUrl: "/images/testimonials/angela-l.png?v=2",
  },
  {
    id: 3,
    name: "Derek M.",
    title: "White Belt (2 stripes)",
    content: "I used to feel overwhelmed trying to keep up with every technique, but now I only focus on what's in my playbook. No more YouTube rabbit holes.",
    rating: 5,
    imageUrl: "/images/testimonials/derek-m.png?v=2",
  },
  {
    id: 4,
    name: "Maya S.",
    title: "Blue Belt Competitor",
    content: "I have a great coach, but sometimes I want to see a technique from different angles. Having the ability to save all the different youtube videos I find in a single technique spot is super helpful.",
    rating: 5,
    imageUrl: "/images/testimonials/maya-s.png?v=2",
  },
  {
    id: 5,
    name: "Nico V.",
    title: "White Belt, 8 months in",
    content: "I won my first tournament, This really helped it all come together.",
    rating: 5,
    imageUrl: "/images/testimonials/nico-v.png?v=2",
  },
  {
    id: 6,
    name: "Sarah K.",
    title: "New Purple Belt",
    content: "This app speaks my language. I'm a visual learner, so being able to see how each position branches into different attacks makes it all feel way less overwhelming.",
    rating: 5,
    imageUrl: "/images/testimonials/sarah-k.png?v=2",
  },
  {
    id: 7,
    name: "Coach Mike D.",
    title: "Black Belt, Academy Owner",
    content: "As a coach, the ability to create and share custom game plans is a game-changer. I built templates for my competition students and now we're all speaking the same strategic language. It's a clean way to order the chaos.",
    rating: 5,
    imageUrl: "/images/testimonials/coach-mike.png?v=2",
  },
  {
    id: 8,
    name: "Jake S.",
    title: "Brown Belt",
    content: "It's fire, oss",
    rating: 5,
    imageUrl: "/images/testimonials/jake-s.png?v=2",
  }
];

const TestimonialSection = () => {
  const isMobile = useIsMobile();
  
  const renderTestimonialCard = (testimonial: Testimonial) => (
    <Card 
      key={testimonial.id} 
      className={`p-6 bg-white shadow-md rounded-xl border-0 transition-all hover:shadow-lg ${
        isMobile ? 'mx-3 min-w-[280px] max-w-[280px]' : 'mx-3 min-w-[350px] max-w-[350px]'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="mb-4 text-bjj-purple">
          <Quote className="w-8 h-8" />
        </div>
        <p className="text-neutral-700 mb-4 flex-grow">{testimonial.content}</p>
        <div className="flex items-center mt-auto">
          <div className="w-12 h-12 rounded-full overflow-hidden mr-4 bg-gray-100">
            <img
              src={testimonial.imageUrl}
              alt={testimonial.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
          <div>
            <h4 className="font-semibold">{testimonial.name}</h4>
            <p className="text-sm text-neutral-500">{testimonial.title}</p>
          </div>
          <div className="ml-auto flex">
            {[...Array(testimonial.rating)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-bjj-purple text-bjj-purple" />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
  
  return (
    <section className="py-16 bg-gradient-to-b from-white to-neutral-100">
      <div className="container-padding">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="bg-bjj-purple/10 text-bjj-purple px-4 py-1.5 rounded-full text-sm font-medium inline-block mb-4">
            TESTIMONIALS
          </span>
          <motion.h2
            className="text-3xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Voices From The Mat
          </motion.h2>
          <p className="text-neutral-600">
            From the ones who stopped guessing
          </p>
        </div>

        <div className="max-w-7xl mx-auto overflow-hidden">
          <Marquee 
            pauseOnHover 
            speed={40} 
            gradient={false}
            className="py-4"
          >
            {testimonials.map(testimonial => (
              <div key={testimonial.id} className="mx-2">
                {renderTestimonialCard(testimonial)}
              </div>
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
